import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, Badge, Skeleton, ErrorState, Button } from '@/components/ui'
import { Shield, Info } from 'lucide-react'
import { api, type OrgRoleInfo } from '@/api/client'

export function TeamRolesPage() {
  const { orgSlug } = useParams()
  const [roles, setRoles] = useState<OrgRoleInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchRoles = async () => {
      if (!orgSlug) return
      try {
        setError(null)
        const response = await api.orgs.listRoles(orgSlug)
        setRoles(response.roles)
      } catch (err) {
        console.error('Failed to fetch roles:', err)
        setError(err instanceof Error ? err.message : 'Failed to load roles')
      } finally {
        setIsLoading(false)
      }
    }
    fetchRoles()
  }, [orgSlug])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-start gap-3 rounded-lg bg-[var(--bg-elevated)] p-4 border border-[var(--border-subtle)]">
          <Skeleton className="h-5 w-5" />
          <div className="flex-1">
            <Skeleton className="h-4 w-64 mb-2" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-24 mb-2" />
                    <Skeleton className="h-3 w-48 mb-3" />
                    <div className="flex gap-2">
                      <Skeleton className="h-5 w-20" />
                      <Skeleton className="h-5 w-24" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return <ErrorState title="Failed to load roles" description={error} action={<Button onClick={() => window.location.reload()}>Try Again</Button>} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-lg bg-[var(--bg-elevated)] p-4 border border-[var(--border-subtle)]">
        <Info className="h-5 w-5 text-[var(--accent)] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-[var(--text-primary)]">Organization roles control access to org-level features</p>
          <p className="text-sm text-[var(--text-secondary)] mt-1">For app-specific roles, configure them in each app's Roles tab.</p>
        </div>
      </div>

      <div className="space-y-4">
        {roles.map((role) => (
          <Card key={role.id}>
            <CardContent className="py-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-[var(--bg-elevated-2)] p-2">
                    <Shield className="h-5 w-5 text-[var(--text-secondary)]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-[var(--text-primary)]">{role.name}</h3>
                      {role.is_system && <Badge variant="info">System</Badge>}
                    </div>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">{role.description}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {role.scopes.map((scope) => (
                        <Badge key={scope} variant="default">{scope}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-sm text-[var(--text-muted)] text-center py-4">
        Custom organization roles coming soon. For now, use the built-in system roles.
      </p>
    </div>
  )
}
